import { useEffect, type ReactNode } from "react";
import { cn } from "../../utils/cn";

export type ModalSize = "sm" | "md" | "lg" | "xl" | "2xl";

interface ModalProps {
  open: boolean;
  onClose: () => void;
  title?: ReactNode;
  description?: ReactNode;
  children: ReactNode;
  footer?: ReactNode;
  size?: ModalSize;
  hideCloseButton?: boolean;
  closeOnBackdrop?: boolean;
}

const SIZES: Record<ModalSize, string> = {
  sm: "max-w-md",
  md: "max-w-lg",
  lg: "max-w-3xl",
  xl: "max-w-4xl",
  "2xl": "max-w-5xl",
};

export function Modal({
  open,
  onClose,
  title,
  description,
  children,
  footer,
  size = "md",
  hideCloseButton,
  closeOnBackdrop = true,
}: ModalProps) {
  useEffect(() => {
    if (!open) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") onClose();
    };
    document.addEventListener("keydown", onKey);
    document.body.style.overflow = "hidden";
    return () => {
      document.removeEventListener("keydown", onKey);
      document.body.style.overflow = "";
    };
  }, [open, onClose]);

  if (!open) return null;

  return (
    <div className="no-print fixed inset-0 z-50 flex items-center justify-center p-4">
      <div
        className="absolute inset-0 bg-foreground-950/40 backdrop-blur-sm"
        onClick={() => closeOnBackdrop && onClose()}
      />
      <div
        role="dialog"
        aria-modal="true"
        className={cn("relative z-10 flex max-h-[88vh] w-full flex-col overflow-hidden rounded-lg border border-background-200 bg-background-50 shadow-xl", SIZES[size])}
      >
        {(title || !hideCloseButton) && (
          <div className="flex items-start justify-between gap-4 border-b border-background-100 px-5 py-4">
            <div>
              {title ? <h2 className="text-base font-semibold text-foreground-900">{title}</h2> : null}
              {description ? <p className="mt-0.5 text-sm text-foreground-600">{description}</p> : null}
            </div>
            {!hideCloseButton && (
              <button type="button" onClick={onClose} aria-label="Close" className="flex h-8 w-8 flex-none cursor-pointer items-center justify-center rounded-md text-foreground-500 hover:bg-background-100 hover:text-foreground-800">
                <i className="ri-close-line text-lg" />
              </button>
            )}
          </div>
        )}
        <div className="flex-1 overflow-auto px-5 py-4">{children}</div>
        {footer ? <div className="flex justify-end gap-2 border-t border-background-100 px-5 py-3.5">{footer}</div> : null}
      </div>
    </div>
  );
}